import { Util } from "@/helpers"
import {
  AuthorizationMiddleware,
  BaseUrlMiddleware,
  CacheResponseMiddleware,
  ExposeRefreshTokenMiddleware,
  JsonRequestMiddleware,
  LogRequestMiddleware,
  RefreshTokenMiddleware,
  RequestExecutor,
  zodSchema,
  type ChangePasswordRequest,
  type CheckTokenRequest,
  type GetMessageListRequest,
  type GoogleSignInOptions,
  type IAuthenticationState,
  type IRequestMiddleware,
  type LoginRequest,
  type LoginWithOneTimeCodeRequest,
  type GetContinuationActionRequest,
  type MemberSession,
  type PostMessage,
  type MicrosoftSignInOptions,
  type RefreshToken,
  type RegisterRequest,
  type CreateOneTimeCodeRequest,
  type RevokeSessionRequest,
  type SignInWithGoogleRequest,
  type SignInWithMicrosoftRequest,
  type UpdateMemberRequest,
  type CreateThreadRequest,
  type SendMessageRequest,
  type GetMessageRequest,
  type MarkMesssageReadRequest,
  type GetThreadDetailRequest,
  type SubscribeToThreadUpdatesRequest,
  type CheckThreadExistsRequest,
  type UploadThreadFilesRequest,
  type PatchPostNotificationPreferenceRequest,
  type PatchConversationNotificationPreferenceRequest,
  type SetPasswordRequest,
  type UpdateProfilePictureRequest,
  type ContinuationAction,
  type RegistrationDefaultsResponse,
  type GetRegistrationDefaultsRequest,
  type GetMessageCountsRequest,
  type GetStudentContactsRequest,
  type GetStaffRecipientRequest,
  type SearchThreadRequest,
  type GetMessageContentRequest,
  type HeartMessageRequest,
  type GetLatestFilesRequest,
  type MessageFile,
  type FileListing,
  type GetSchoolsRequest,
  type GetSchoolRequest,
  type RequestInitExtended,
  type GetDistrictsRequest,
  type GetAnnouncementsRequest,
  type GetGoogleCalendarEvents,
  type CalendarInfo,
  type EventInfo,
  type GetGoogleCalendarEvent,
  LoadingSpinnerMiddleware,
  RequestOptions,
  type GetDistrictRequest,
  type Group,
  type GetAvailableGroupsRequest,
  type SubscribeToGroupRequest,
  type UnsubscribeFromGroupRequest,
  type GetGroupRequest,
  type GetAnnouncementRequest,
  type ForgotPasswordStepOneRequest,
  type ForgotPasswordStepTwoRequest,
  type GetReplyToRecipientsRequest,
  type DeleteMobileAppRegistrationRequest,
} from "@/api"
import type { InjectionKey } from "vue"
import { z, type ZodTypeAny } from "zod"
import { Announcement, announcementSchemas, ConversationDetail, ConversationOverview, conversationSchemas, District, districtSchemas, MemberProfile, memberSchemas, MobileAppRegistration, Recipient, School, schoolSchemas, StaffRecipient, StatusCode, StudentContact, studentSchemas, type ConversationAttachment, type SentMessage } from "@/models"
import {EventSource} from 'eventsource'
import { omit, pick } from "remeda"

export interface IApplicationApi {
  // Announcements
  getAnnouncements(request: GetAnnouncementsRequest, overrides?: RequestInitExtended): Promise<Announcement[]>
  getAnnouncement(request: GetAnnouncementRequest): Promise<Announcement | undefined>

  // Messages
  getMessageList(request: GetMessageListRequest, overrides?: RequestInitExtended): Promise<PostMessage[]>
  getMessage(request: GetMessageRequest): Promise<PostMessage | undefined>
  getMessageCountByCourse(request: GetMessageCountsRequest, overrides?: RequestInitExtended): Promise<Map<string, number>>
  getMessageCountByGroup(request: GetMessageCountsRequest, overrides?: RequestInitExtended): Promise<Map<number, number>>
  getMessageContent(request: GetMessageContentRequest): Promise<string | undefined>
  getLatestFiles(request: GetLatestFilesRequest, overrides?: RequestInitExtended): Promise<MessageFile[]>
  getAllFiles(): Promise<FileListing[]>
  heartMessage(request: HeartMessageRequest): Promise<void>
  getLatestAlert(overrides?: RequestInitExtended): Promise<PostMessage | undefined>

  // Member/Auth
  login(request: LoginRequest): Promise<RefreshToken>
  loginWithOneTimeCode(request: LoginWithOneTimeCodeRequest): Promise<RefreshToken>
  forgotPasswordStepOne(request: ForgotPasswordStepOneRequest): Promise<void>
  forgotPasswordStepTwo(request: ForgotPasswordStepTwoRequest): Promise<void>
  getContinuationAction(request: GetContinuationActionRequest): Promise<ContinuationAction>
  getRegistrationDefaults(request: GetRegistrationDefaultsRequest): Promise<RegistrationDefaultsResponse>
  getMicrosoftSignInOptions(): Promise<MicrosoftSignInOptions>
  signInWithMicrosoft(request: SignInWithMicrosoftRequest): Promise<RefreshToken>
  getGoogleSignInOptions(): Promise<GoogleSignInOptions>
  signInWithGoogle(request: SignInWithGoogleRequest): Promise<RefreshToken>
  logOut(): Promise<void>
  register(request: RegisterRequest): Promise<RefreshToken>
  getProfile(): Promise<MemberProfile>
  hasPassword(): Promise<boolean>
  createOneTimeCode(request: CreateOneTimeCodeRequest): Promise<void>
  checkToken(request: CheckTokenRequest): Promise<boolean>
  getSessions(): Promise<MemberSession[]>
  revokeSession(request: RevokeSessionRequest): Promise<void>
  getMobileAppRegistrations(): Promise<MobileAppRegistration[]>
  deleteMobileAppRegistration(request: DeleteMobileAppRegistrationRequest): Promise<void>
  changePassword(request: ChangePasswordRequest): Promise<void>
  setPassword(request: SetPasswordRequest): Promise<void>
  updateMember(request: UpdateMemberRequest): Promise<void>
  updateMemberProfilePicture(request: UpdateProfilePictureRequest): Promise<void>
  updatePostNotificationPreference(request: PatchPostNotificationPreferenceRequest): Promise<void>
  updateConversationNotificationPreference(request: PatchConversationNotificationPreferenceRequest): Promise<void>

  // Students
  getStudentContacts(request: GetStudentContactsRequest): Promise<StudentContact[]>

  // Schools
  getSchool(request: GetSchoolRequest): Promise<School>
  getSchools(request: GetSchoolsRequest): Promise<School[]>

  // Districts
  getDistrict(request: GetDistrictRequest): Promise<District | undefined>
  getDistricts(request: GetDistrictsRequest, overrides?: RequestInitExtended): Promise<District[]>

    // Calendar
  getGoogleCalendarEvents(request: GetGoogleCalendarEvents, overrides?: RequestInitExtended): Promise<CalendarInfo[]>
  getGoogleCalendarEvent(request: GetGoogleCalendarEvent): Promise<EventInfo>

  // Threaded Messages
  possibleRecipients(): Promise<Recipient[]>
  getStaffRecipient(request: GetStaffRecipientRequest): Promise<StaffRecipient>
  getReplyToRecipients(request: GetReplyToRecipientsRequest): Promise<StaffRecipient[]>
  nextThreadId(): Promise<number>
  createThread(request: CreateThreadRequest): Promise<void>
  sendMessage(request: SendMessageRequest): Promise<void>
  markRead(request: MarkMesssageReadRequest): Promise<void>
  checkThreadExists(request: CheckThreadExistsRequest): Promise<boolean>
  getThread(request: GetThreadDetailRequest): Promise<ConversationDetail>
  getThreads(): Promise<ConversationOverview[]>
  getUnreadCount(): Promise<number>
  subscribeToThreadOverviewUpdates() : EventSource
  subscribeToThreadUpdates(request: SubscribeToThreadUpdatesRequest) : EventSource
  uploadThreadFiles(request: UploadThreadFilesRequest): Promise<ConversationAttachment[]>
  searchThread(request: SearchThreadRequest): Promise<SentMessage[]>

  // Groups
  getGroup(request: GetGroupRequest): Promise<Group | undefined>
  getSubscribedGroups(overrides?: RequestInitExtended): Promise<Group[]>
  getAvailableGroups(request: GetAvailableGroupsRequest): Promise<Group[]>
  subscribeToGroup(request: SubscribeToGroupRequest): Promise<void>
  unsubscribeFromGroup(request: UnsubscribeFromGroupRequest): Promise<void>
}

export class ApplicationApi implements IApplicationApi {
  private readonly auth: IAuthenticationState
  private readonly basePipeline: IRequestMiddleware
  private readonly signInPipeline: IRequestMiddleware
  private readonly authenticatedPipeline: IRequestMiddleware

  constructor(baseUrl: string, auth: IAuthenticationState) {
    this.auth = auth
    this.basePipeline = ApplicationApi.createBasePipeline(auth, baseUrl)
    this.signInPipeline = ApplicationApi.createSigninPipeline(this.basePipeline)
    this.authenticatedPipeline = ApplicationApi.createAuthorizedPipeline(this.basePipeline, auth)
  }


  async getAnnouncements(request: GetAnnouncementsRequest, overrides?: RequestInitExtended): Promise<Announcement[]> {
    const results = await this.processRequest(this.authenticatedPipeline, z.array(announcementSchemas.Announcement), `/api/announcements${this.toQueryString(request)}`, RequestOptions.cachedWithLabel(LL => LL.LOADING_DATA(LL.ANNOUNCEMENTS()), overrides))
    return results.map(r => new Announcement(r))
  }

  async getAnnouncement(request: GetAnnouncementRequest): Promise<Announcement | undefined> {
    const result = await this.processRequest(this.authenticatedPipeline, announcementSchemas.Announcement.optional(), `/api/announcements/${request.announcementId}${this.toQueryString(omit(request, ['announcementId']))}`, RequestOptions.cachedWithLabel(LL => LL.LOADING_DATA(LL.ANNOUNCEMENT_DETAILS())))

    if (result)
      return new Announcement(result)
  }

  getMessageList(request: GetMessageListRequest, overrides?: RequestInitExtended): Promise<PostMessage[]> {
    return this.processRequest(this.authenticatedPipeline, z.array(zodSchema.MessageSchema), `/api/messages${this.toQueryString(request)}`, RequestOptions.cachedWithLabel(LL => LL.LOADING_DATA(LL.POSTS()), overrides))
  }

  getMessage(request: GetMessageRequest): Promise<PostMessage | undefined> {
    return this.processRequest(this.authenticatedPipeline, zodSchema.MessageSchema.optional(), `/api/message/${request.messageId}`, RequestOptions.cachedWithLabel(LL => LL.LOADING_DATA(LL.MESSAGE())))
  }

  async getMessageCountByCourse(request: GetMessageCountsRequest, overrides?: RequestInitExtended): Promise<Map<string, number>> {
    const results = await this.processRequest(this.authenticatedPipeline, z.record(z.string(), z.number()), `/api/messages/count-by-course${this.toQueryString(request)}`, RequestOptions.cachedWithLabel(LL => LL.LOADING_DATA(LL.COURSES()), overrides))

    return new Map(Object.entries(results))
  }

  async getMessageCountByGroup(request: GetMessageCountsRequest, overrides?: RequestInitExtended): Promise<Map<number, number>> {
    const results = await this.processRequest(this.authenticatedPipeline, z.record(z.string(), z.number()), `/api/messages/count-by-group${this.toQueryString(request)}`, RequestOptions.cachedWithLabel(LL => LL.LOADING_DATA(LL.GROUPS()), overrides))

    return new Map(Object.entries(results).map(e => [Number.parseInt(e[0]), e[1]]))
  }

  getLatestAlert(overrides?: RequestInitExtended): Promise<PostMessage | undefined> {
    return this.processRequest(this.authenticatedPipeline, zodSchema.MessageSchema.optional(), `/api/messages/latest-alert`, RequestOptions.cached().merge(overrides))
  }

  getMessageContent(request: GetMessageContentRequest): Promise<string | undefined> {
    return this.processRequest(this.authenticatedPipeline, z.string().optional(), `/api/message/${request.messageId}/content?culture=${request.culture}`, RequestOptions.cachedWithLabel(LL => LL.LOADING_DATA(LL.POST_CONTENT())))
  }

  getLatestFiles(request: GetLatestFilesRequest, overrides?: RequestInitExtended): Promise<MessageFile[]> {
    return this.processRequest(this.authenticatedPipeline, z.array(zodSchema.MessageFile), `/api/messages/latest-files${this.toQueryString(request)}`, RequestOptions.cachedWithLabel(LL => LL.LOADING_DATA(LL.LATEST_FILES()), overrides))
  }

  getAllFiles(): Promise<FileListing[]> {
    return this.processRequest(this.authenticatedPipeline, z.array(zodSchema.FileListing), `/api/messages/files`, RequestOptions.cachedWithLabel(LL => LL.LOADING_DATA(LL.FILES())))
  }

  getGoogleCalendarEvents(request: GetGoogleCalendarEvents, overrides?: RequestInitExtended): Promise<CalendarInfo[]> {
    const params = {
      ids: request.ids,
      timeMin: request.timeMin.toISOString(),
      timeMax: request.timeMax.toISOString()
    }
    return this.processRequest(this.authenticatedPipeline, z.array(zodSchema.CalendarInfo), `/api/calendar/calendar-events${this.toQueryString(params)}`, RequestOptions.cachedWithLabel(LL => LL.LOADING_DATA(LL.CALENDAR_EVENTS()), overrides))
  }

  getGoogleCalendarEvent(request: GetGoogleCalendarEvent): Promise<EventInfo> {
    return this.processRequest(this.authenticatedPipeline, zodSchema.EventInfo, `/api/calendar/calendar-event${this.toQueryString(request)}`, RequestOptions.cachedWithLabel(LL => LL.LOADING_DATA(LL.CALENDAR_EVENT_DETAILS())))
  }

  heartMessage(request: HeartMessageRequest): Promise<void> {
    return this.processRequest(this.authenticatedPipeline, z.any(), `/api/message/${request.messageId}/heart`, {
      method: 'POST',
    })
  }

  async login(request: LoginRequest): Promise<RefreshToken> {
    const response = await this.processRequest(this.signInPipeline, zodSchema.RefreshToken, `/api/member/login`, {
      method: 'POST',
      body: JSON.stringify(request),
    })

    this.auth.setToken(response.accessToken)

    return response
  }

  async loginWithOneTimeCode(request: LoginWithOneTimeCodeRequest): Promise<RefreshToken> {
    const response = await this.processRequest(this.signInPipeline, zodSchema.RefreshToken, `/api/member/login-code`, {
      method: 'POST',
      body: JSON.stringify(request),
    })

    this.auth.setToken(response.accessToken)

    return response
  }

  forgotPasswordStepOne(request: ForgotPasswordStepOneRequest): Promise<void> {
    return this.processRequest(this.basePipeline, z.any(), `/api/member/forgot-password${this.toQueryString(request)}`, {
      ... RequestOptions.create().noCache().noSpinner().build(),
      method: 'POST'
    })
  }

  forgotPasswordStepTwo(request: ForgotPasswordStepTwoRequest): Promise<void> {
    return this.processRequest(this.basePipeline, z.any(), `/api/member/forgot-password/complete`, {
      ... RequestOptions.create().noCache().noSpinner().build(),
      body: JSON.stringify(request),
      method: 'POST'
    })
  }

  getContinuationAction(request: GetContinuationActionRequest): Promise<ContinuationAction> {
    return this.processRequest(this.basePipeline, zodSchema.ContinuationAction, `/api/member/continuation-action${this.toQueryString(request)}`, RequestOptions.create().noCache().noSpinner().build())
  }

  getRegistrationDefaults(request: GetRegistrationDefaultsRequest): Promise<RegistrationDefaultsResponse> {
    return this.processRequest(this.basePipeline, zodSchema.RegistrationDefaultsResponse, `/api/member/registration-defaults${this.toQueryString(request)}`)
  }

  async hasPassword(): Promise<boolean> {
    const response = await this.processRequest(this.authenticatedPipeline, zodSchema.GetMemberHasPasswordResponse, `/api/member/has-password`, RequestOptions.create().noCache().noSpinner().build())
    return response.hasPassword
  }

  getMicrosoftSignInOptions(): Promise<MicrosoftSignInOptions> {
    return this.processRequest(this.basePipeline, zodSchema.MicrosoftSignInOptions, `/api/member/sign-in-microsoft/options`)
  }

  async signInWithMicrosoft(request: SignInWithMicrosoftRequest): Promise<RefreshToken> {
    const response = await this.processRequest(this.signInPipeline, zodSchema.RefreshToken, `/api/member/sign-in-microsoft`, {
      method: 'POST',
      body: JSON.stringify(request)
    })

    this.auth.setToken(response.accessToken)

    return response
  }

  getGoogleSignInOptions(): Promise<GoogleSignInOptions> {
    return this.processRequest(this.basePipeline, zodSchema.GoogleSignInOptions, `/api/member/sign-in-google/options`)
  }

  async signInWithGoogle(request: SignInWithGoogleRequest): Promise<RefreshToken> {
    const response = await this.processRequest(this.signInPipeline, zodSchema.RefreshToken, `/api/member/sign-in-google`, {
      method: 'POST',
      body: JSON.stringify(request)
    })

    this.auth.setToken(response.accessToken)

    return response
  }

  async logOut(): Promise<void> {
    await this.processRequest(this.authenticatedPipeline, z.any(), `/api/member/logout`, {
      ...RequestOptions.clearAllCache().build(),
      method: 'POST',
      credentials: 'include',
    })
    this.auth.setToken(undefined);
  }

  async register(request: RegisterRequest): Promise<RefreshToken> {
    const response = await this.processRequest(this.signInPipeline, zodSchema.RefreshToken, `/api/member/register`, {
      method: 'POST',
      body: JSON.stringify(request),
    })

    this.auth.setToken(response.accessToken)

    return response
  }

  async getProfile(): Promise<MemberProfile> {
    const response = await this.processRequest(this.authenticatedPipeline, memberSchemas.MemberProfile, `/api/member/profile`, RequestOptions.cachedWithLabel(LL => LL.LOADING_DATA(LL.MEMBER_PROFILE())))
    return new MemberProfile(response)
  }

  async getStudentContacts(request: GetStudentContactsRequest): Promise<StudentContact[]> {
    const results = await this.processRequest(this.authenticatedPipeline, z.array(studentSchemas.StudentContact), `/api/student/${request.studentId}/contacts`, RequestOptions.cached().build())
    return results.map(r => new StudentContact(r))
  }

  async getSchool(request: GetSchoolRequest): Promise<School> {
    const result = await this.processRequest(this.authenticatedPipeline, schoolSchemas.School, `/api/school/${request.id}${this.toQueryString(pick(request, ['includeCmsData']))}`, RequestOptions.cachedWithLabel(LL => LL.LOADING_DATA(LL.SCHOOLS())))
    return new School(result)
  }

  async getSchools(request: GetSchoolsRequest): Promise<School[]> {
    const results = await this.processRequest(this.authenticatedPipeline, z.array(schoolSchemas.School), `/api/schools${this.toQueryString(request)}`, RequestOptions.cachedWithLabel(LL => LL.LOADING_DATA(LL.SCHOOLS())))
    return results.map(r => new School(r))
  }

  async getDistrict(request: GetDistrictRequest): Promise<District | undefined> {
    const result = await this.processRequest(this.authenticatedPipeline, districtSchemas.District.optional(), `/api/district/${request.id}/${this.toQueryString(omit(request, ['id']))}`, RequestOptions.cachedWithLabel(LL => LL.LOADING_DATA(LL.DISTRICT_INFO())))
    if (result) return new District(result)
  }

  async getDistricts(request: GetDistrictsRequest, overrides?: RequestInitExtended): Promise<District[]> {
    const results = await this.processRequest(this.authenticatedPipeline, z.array(districtSchemas.District), `/api/districts${this.toQueryString(request)}`, RequestOptions.cachedWithLabel(LL => LL.LOADING_DATA(LL.DISTRICT_INFO()), overrides))
    return results.map(r => new District(r))
  }

  createOneTimeCode(request: CreateOneTimeCodeRequest): Promise<void> {
    return this.processRequest(this.basePipeline, z.any(), `/api/member/create-one-time-code`, {
      method: 'POST',
      body: JSON.stringify(request),
    })
  }

  checkToken(request: CheckTokenRequest): Promise<boolean> {
    return this.processRequest(this.basePipeline, z.boolean(), `/api/member/check-token${this.toQueryString(request)}`)
  }

  getSessions(): Promise<MemberSession[]> {
    return this.processRequest(this.authenticatedPipeline, z.array(zodSchema.MemberSession), `/api/member/sessions`)
  }

  revokeSession(request: RevokeSessionRequest): Promise<void> {
    return this.processRequest(this.authenticatedPipeline, z.any(), `/api/member/session`, {
      method: 'DELETE',
      body: JSON.stringify(request),
    })
  }

  getMobileAppRegistrations(): Promise<MobileAppRegistration[]> {
    return this.processRequest(this.authenticatedPipeline, z.array(memberSchemas.MobileAppRegistration).transform(results => results.map(r => new MobileAppRegistration(r))), `/api/member/mobile-app-registrations`)
  }

  deleteMobileAppRegistration(request: DeleteMobileAppRegistrationRequest): Promise<void> {
    return this.processRequest(this.authenticatedPipeline, z.any(), `/api/member/mobile-app-registration/${request.deviceId}`, {
      method: 'DELETE'
    })
  }

  changePassword(request: ChangePasswordRequest): Promise<void> {
    return this.processRequest(this.authenticatedPipeline, z.any(), `/api/member/change-password`, {
      method: 'POST',
      body: JSON.stringify(request),
    })
  }

  setPassword(request: SetPasswordRequest): Promise<void> {
    return this.processRequest(this.authenticatedPipeline, z.any(), `/api/member/set-password`, {
      method: 'POST',
      body: JSON.stringify(request),
    })
  }

  updateMember(request: UpdateMemberRequest): Promise<void> {
    return this.processRequest(this.authenticatedPipeline, z.any(), `/api/member`, {
      method: 'PATCH',
      body: JSON.stringify(request),
    })
  }

  updateMemberProfilePicture(request: UpdateProfilePictureRequest): Promise<void> {
    const formData = new FormData()

    if (request.file) {
      formData.append('file', request.file)
    }

    return this.processRequest(this.authenticatedPipeline, z.any(), `/api/member/profile-picture`, {
      ...RequestOptions.create().doNotSetContentType().build(),
      method: 'POST',
      body: formData,
    })
  }

  updatePostNotificationPreference(request: PatchPostNotificationPreferenceRequest): Promise<void> {
    return this.processRequest(this.authenticatedPipeline, z.any(), `/api/member/post-notification-preferences/${request.preferenceId}`, {
      ...RequestOptions.noSpinner().build(),
      method: 'PATCH',
      body: JSON.stringify(request),
    })
  }

  updateConversationNotificationPreference(request: PatchConversationNotificationPreferenceRequest): Promise<void> {
    return this.processRequest(this.authenticatedPipeline, z.any(), `/api/member/conversation-notification-preferences/${request.preferenceId}`, {
      ...RequestOptions.noSpinner().build(),
      method: 'PATCH',
      body: JSON.stringify(request),
    })
  }

  async possibleRecipients(): Promise<Recipient[]> {
    const response = await this.processRequest(this.authenticatedPipeline, z.array(conversationSchemas.RecipientSchema), `/api/thread/possible-recipients`, RequestOptions.cachedWithLabel(LL => LL.LOADING_DATA(LL.RECIPIENTS())))
    return response.map(Recipient.Create)
  }

  async getStaffRecipient(request: GetStaffRecipientRequest): Promise<StaffRecipient> {
    const response = await this.processRequest(this.authenticatedPipeline, conversationSchemas.StaffRecipientSchema.extend({
      $type: z.literal('Staff').default('Staff')
    }), `/api/staff/${request.staffId}/as-recipient`)

    return new StaffRecipient(response)
  }

  async getReplyToRecipients(request: GetReplyToRecipientsRequest): Promise<StaffRecipient[]> {
    const response = await this.processRequest(this.authenticatedPipeline, z.array(conversationSchemas.StaffRecipientSchema.extend({
      $type: z.literal('Staff').default('Staff')
    })), `/api/messages/${request.messageId}/reply-to-recipients`)

    return response.map(r => new StaffRecipient(r))
  }

  nextThreadId(): Promise<number> {
    return this.processRequest(this.authenticatedPipeline, z.number(), `/api/thread/next-id`, RequestOptions.create().noCache().noSpinner().build())
  }

  createThread(request: CreateThreadRequest): Promise<void> {
    return this.processRequest(this.authenticatedPipeline, z.any(), `/api/thread`, {
      method: 'POST',
      body: JSON.stringify(request),
    })
  }

  sendMessage(request: SendMessageRequest): Promise<void> {
    return this.processRequest(this.authenticatedPipeline, z.any(), `/api/thread/${request.threadId}/message`, {
      ...RequestOptions.noSpinner().build(),
      method: 'POST',
      body: JSON.stringify(request),
    })
  }

  markRead(request: MarkMesssageReadRequest): Promise<void> {
    return this.processRequest(this.authenticatedPipeline, z.any(), `/api/thread/${request.threadId}/message/${request.messageId}/mark-read`, {
      ...RequestOptions.noSpinner().build(),
      method: 'POST',
    })
  }

  checkThreadExists(request: CheckThreadExistsRequest): Promise<boolean> {
    return this.processRequest(this.authenticatedPipeline, z.boolean(), `/api/thread/${request.threadId}/exists`, RequestOptions.create().noCache().noSpinner().build())
  }

  async getThread(request: { threadId: number }): Promise<ConversationDetail> {
    const response = await this.processRequest(this.authenticatedPipeline, conversationSchemas.ConversationDetailSchema, `/api/thread/${request.threadId}`)
    return new ConversationDetail(response)
  }

  async getThreads(): Promise<ConversationOverview[]> {
    const response = await this.processRequest(this.authenticatedPipeline, z.array(conversationSchemas.ConversationOverviewSchema), `/api/threads`)
    return response.map(r => new ConversationOverview(r))
  }

  getUnreadCount(): Promise<number> {
    return this.processRequest(this.authenticatedPipeline, z.number(), `/api/threads/unread/count`)
  }

  subscribeToThreadOverviewUpdates() : EventSource {
    return new EventSource('', {
      fetch: (_, init) => this.authenticatedPipeline.process('/api/thread/subscribe', {
        ...RequestOptions.noSpinner().build(),
        ...init,
      })
    })
  }

  subscribeToThreadUpdates(request: { threadId: number }) : EventSource {
    return new EventSource('', {
      fetch: (_, init) => this.authenticatedPipeline.process(`/api/thread/${request.threadId}/subscribe`, {
        ...RequestOptions.noSpinner().build(),
        ...init,
      })
    })
  }

  async uploadThreadFiles(request: UploadThreadFilesRequest): Promise<ConversationAttachment[]> {
    const response = await this.processRequest(this.authenticatedPipeline, z.array(conversationSchemas.ConversationAttachmentSchema), `/api/files/thread/${request.threadId}`, {
      ...RequestOptions.create().noSpinner().doNotSetContentType().build(),
      method: 'POST',
      body: this.filesToFormData('files', request.files)
    })

    return response
  }

  searchThread(request: SearchThreadRequest): Promise<SentMessage[]> {
    return this.processRequest(this.authenticatedPipeline, z.array(conversationSchemas.SentMessageSchema), `/api/threads/search${this.toQueryString(request)}`)
  }

  getGroup(request: GetGroupRequest): Promise<Group | undefined> {
    return this.processRequest(this.authenticatedPipeline, zodSchema.Group.optional(), `/api/groups/${request.groupId}`)
  }

  getSubscribedGroups(overrides?: RequestInitExtended): Promise<Group[]> {
    return this.processRequest(this.authenticatedPipeline, z.array(zodSchema.Group), `/api/groups/subscribed`, overrides)
  }

  getAvailableGroups(request: GetAvailableGroupsRequest): Promise<Group[]> {
    return this.processRequest(this.authenticatedPipeline, z.array(zodSchema.Group), `/api/groups/available${this.toQueryString(request)}`)
  }

  subscribeToGroup(request: SubscribeToGroupRequest): Promise<void> {
    return this.processRequest(this.authenticatedPipeline, z.any(), `/api/groups/${request.groupId}/subscribe`, {
      method: 'POST'
    })
  }

  unsubscribeFromGroup(request: UnsubscribeFromGroupRequest): Promise<void> {
    return this.processRequest(this.authenticatedPipeline, z.any(), `/api/groups/${request.groupId}/unsubscribe`, {
      method: 'POST'
    })
  }

  private static createBasePipeline(auth: IAuthenticationState, baseUrl: string) {
    return new RefreshTokenMiddleware(new JsonRequestMiddleware(new BaseUrlMiddleware(new LogRequestMiddleware(new CacheResponseMiddleware(new LoadingSpinnerMiddleware(new RequestExecutor()))), baseUrl)), auth)
  }

  private static createAuthorizedPipeline(next: IRequestMiddleware, auth: IAuthenticationState) {
    return new AuthorizationMiddleware(next, auth)
  }

  private static createSigninPipeline(next: IRequestMiddleware) {
    return new ExposeRefreshTokenMiddleware(next)
  }

  private static extractResourceTag(path: string): string {
    const segments = path.split('?')[0].split('/').filter(Boolean)
    const resourceSegment = segments[0] === 'api' ? (segments[1] ?? '') : (segments[0] ?? '')
    return resourceSegment.endsWith('s') ? resourceSegment.slice(0, -1) : resourceSegment
  }

  private async processRequest<T extends ZodTypeAny>(handler: IRequestMiddleware, schema: T, input: RequestInfo | URL, init?: RequestInitExtended, cacheOptions?: { tags?: string[], invalidate?: string[] }): Promise<z.infer<T>> {
    const defaultTag = ApplicationApi.extractResourceTag(input.toString())
    const resolvedCache = {
      ...init?.middleware?.cache,
      tags: cacheOptions?.tags ?? [defaultTag],
      invalidate: cacheOptions?.invalidate ?? [defaultTag],
    }

    // Use the specified middleware pipeline to fetch the response
    const response = await handler.process(input, {
      ...init,
      middleware: { ...init?.middleware, cache: resolvedCache },
    });

    // Check if the response has content before we move on to the content processing logic
    if (!this.hasContent(response)) {
      // If this is an OK response with no content, just return. OK reponses are allowed to have no content.
      if (response.ok)
        return undefined;
      // If the response has no content and is not OK, this is an unexpected condition. API errors should always return a ProblemDetails response.
      else
        throw new Error('Unexpected zero-length content in error response. All errors should return a ProblemDetails instance.')
    }

    // Get the message content as text or json, depending on the type
    const contentType = response.headers.get('content-type')
    const content = contentType?.includes('json') ? await response.json() : await response.text()

    // If this is an error response with content, this should be a ProblemDetails object. Regardless, throw the content and let the caller deal with it.
    if (!response.ok)
      throw content

    // If this is an OK response, parse the content. This will throw if the content is not in the expected format.
    return schema.parse(content)
  }

  private hasContent(response: Response) {
    if (response.status === StatusCode.NoContent) return false
    else if (response.headers.get('content-type')) return true

    const contentLengthHeader = response.headers.get('content-length')
    const contentLength = Number.parseInt(contentLengthHeader ?? '0')
    return !Number.isNaN(contentLength) && contentLength !== 0
  }

  private toQueryString(...args: (object | null | undefined)[]) {
    const querystring = args.filter(a => !Util.isNullOrUndefined(a))
        .map(o => Object.entries(o)) // Get all the properties of all the objects
        .flat() // Flatten the properties into a single array
        .filter(p => !Util.isNullOrUndefined(p[1]) && p[1] !== '') // Filter out properties that are null or empty
        .flatMap(p => { // Convert array properties to repeated non-array properties
            if (p[1] instanceof Array) {
                return p[1].filter(v => !Util.isNullOrUndefined(v))
                    .map(v => [p[0], v]);
            }
            else {
                if (p[1] instanceof Date) {
                    // Convert dates to UTC strings to be understood by the server
                    return [[p[0], Util.toISOStringLocal(p[1])]];
                }
                return [p];
            }
        })
        .map(p => `${encodeURIComponent(p[0].toLowerCase())}=${encodeURIComponent(p[1])}`) // format as 'prop=value'
        .join('&'); // join with a '&'

      return Util.isNullOrWhitespace(querystring) ? '' : `?${querystring}`
  }

  private filesToFormData(key: string, files: File[]) {
    const formData = new FormData()

    files.forEach(f => formData.append(key, f))

    return formData
  }

  private getFormData(obj: object) {
        const formData = new FormData();

        Object.entries(obj)
            .filter(e => !Util.isNullOrUndefined(e[1]))
            .forEach(e => {
                if (typeof (e[1]) == 'string' || e[1] instanceof Blob) {
                  formData.append(e[0], e[1]);
                }
                else if (typeof (e[1]) === 'boolean' || typeof (e[1]) === 'number') {
                  formData.append(e[0], `${e[1]}`);
                }
                else if (e[1] instanceof Array) {
                  e[1].forEach(v => {
                    if (typeof (v) == 'string') {
                      formData.append(e[0], e[1]);
                    }
                  })
                }
            });

        return formData;
    }
}

export const MEMBER_API: InjectionKey<IApplicationApi> = Symbol('Member API');
